import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { ArrowDown, ArrowRight, Check, ChevronDown } from 'lucide-react'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'
import { setRobotsNoindex } from '@/lib/page-seo'
import { GranGuardMark, GG_EMAIL } from '@/components/granguard/brand'
import '@/styles/lafayette-pitch.css'

/** Lafayette's private proposal. Commercial terms: docs/strategy/lafayette-offer.md.
 * Keep film discussions verbal, center participation conditional, and delivery self-guided.
 * The existing partner gate and host restriction also apply to this route.
 */
export const Route = createFileRoute('/pitch')({ component: PitchPage })

const CENTERS = [
  { name: 'North Potomac Senior Center', location: 'Rockville', operator: 'Montgomery County Recreation' },
  { name: 'Rockville Senior Center', location: 'Rockville', operator: 'City of Rockville' },
  { name: 'Margaret Schweinhaut Senior Center', location: 'Silver Spring', operator: 'Montgomery County Recreation' },
  { name: 'Wheaton Senior Center', location: 'Wheaton', operator: 'Montgomery County Recreation' },
  { name: 'Holiday Park Senior Center', location: 'Silver Spring', operator: 'Montgomery County Recreation' },
  { name: 'Bernice Fonteneau Senior Wellness Center', location: 'Washington, DC · Ward 1', operator: 'DC Aging and Community Living' },
]

const BUNDLES = [
  { name: '1 center', annual: '$2,500', each: '$2,500' },
  { name: '3 centers', annual: '$6,750', each: '$2,250' },
  { name: '6 centers', annual: '$12,000', each: '$2,000' },
  { name: '12 centers', annual: '$24,000', each: '$2,000' },
]

function PitchPage() {
  const partner = COMMUNITY_PARTNER

  useEffect(() => {
    setRobotsNoindex(true)
    return () => setRobotsNoindex(false)
  }, [])

  if (!partner || partner.slug !== 'lafayette') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-semibold text-slate-900">Nothing here.</h1>
        <p className="mt-3 text-lg text-slate-600">
          This page belongs to a partner site. <Link to="/" className="underline">Go to the homepage</Link>.
        </p>
      </div>
    )
  }

  const vars = {
    '--lp-navy': partner.colors.deep,
    '--lp-blue': partner.colors.primary,
    '--lp-accent': partner.colors.primary,
  } as CSSProperties

  return (
    <div className="lafayette-pitch" style={vars}>
      <a href="#proposal" className="lp-skip">Skip to proposal</a>
      <header className="lp-header">
        <div className="lp-container lp-header-inner">
          <a href="#proposal" className="lp-brand" aria-label="Lafayette Federal Credit Union proposal">
            <img src={partner.logoUrl} alt="" width="38" height="38" />
            <span>Lafayette Federal<small>Credit Union</small></span>
          </a>
          <span className="lp-header-note">A proposal from GranGuard</span>
          <Link to="/" className="lp-text-link">Explore the member site <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </header>

      <nav className="lp-nav" aria-label="Proposal sections">
        <div className="lp-container lp-nav-inner">
          <a href="#offer">The member benefit</a>
          <a href="#community">The community</a>
          <a href="#launch">The launch</a>
          <a href="#investment">The investment</a>
        </div>
      </nav>

      <main id="proposal">
        <section className="lp-hero lp-container" aria-labelledby="pitch-title">
          <p className="lp-eyebrow">Lafayette Federal × GranGuard <span> / </span> September 2026</p>
          <h1 id="pitch-title">Make your members<br /><span>harder to scam.</span></h1>
          <p className="lp-hero-copy">Practical scam defense for members, families and local senior centers.</p>
          <a href="#offer" className="lp-button">See the proposal <ArrowDown size={16} aria-hidden="true" /></a>
          <p className="lp-prepared">Prepared for Varun Gupta, Kate Purdy and Lisa Bromley</p>
          <div className="lp-hero-notes" aria-label="Program essentials">
            <span><Check size={15} aria-hidden="true" /> Co-branded</span>
            <span><Check size={15} aria-hidden="true" /> No core integration</span>
            <span><Check size={15} aria-hidden="true" /> Members &amp; families</span>
          </div>
        </section>

        <section className="lp-opportunity" aria-labelledby="opportunity-title">
          <div className="lp-container lp-opportunity-inner">
            <div>
              <p className="lp-eyebrow">The problem</p>
              <h2 id="opportunity-title">Technology alone isn’t enough.</h2>
            </div>
            <div className="lp-opportunity-copy">
              <p>Technology can flag suspicious activity. Scammers use trust, fear and urgency to persuade people to send money themselves.</p>
              <p>Members need the skills to recognize manipulation, resist pressure and pause to verify. That human resilience is what GranGuard builds.</p>
            </div>
          </div>
        </section>

        <section id="offer" className="lp-section lp-container" aria-labelledby="offer-title">
          <SectionHeading number="01" label="The member benefit" id="offer-title" title="Scam defense for the whole family.">
            One Lafayette-branded place to learn, check a suspicious message and find help. For members and their families.
          </SectionHeading>
          <div className="lp-experience">
            <div className="lp-benefits">
              <Benefit number="01" title="Learn the warning signs.">Six short chapters, designed for older adults. How scams work, when to pause and what to do next.</Benefit>
              <Benefit number="02" title="Check before acting.">Plain-English guidance on suspicious links, messages, pictures, recordings and videos, with the evidence explained.</Benefit>
              <Benefit number="03" title="Know where to turn.">A current scam library, with Lafayette’s help lines first and the national reporting services after them.</Benefit>
              <Link to="/" className="lp-text-link lp-demo-link">Explore the member site <ArrowRight size={17} aria-hidden="true" /></Link>
            </div>
            <figure className="lp-product-visual">
              <Link to="/" aria-label="Explore the GranGuard member platform">
                <img src="/partners/lafayette-product-mockup.png" width="691" height="298" loading="lazy" alt="The GranGuard member platform on a tablet beside the optional printed Scam Defense kit." />
              </Link>
              <figcaption>The member platform and the optional printed kit.</figcaption>
            </figure>
          </div>
          <div className="lp-inclusions">
            <div><h3>Something to take home.</h3><p>Optional printed kits and QR cards for your branches, quoted by volume.</p></div>
            <div><h3>Something to share.</h3><p>The same access for parents and adult children.</p></div>
            <div><h3>Something to measure.</h3><p>Engagement summaries of course activity, completions and feedback. Totals, never member records.</p></div>
          </div>
          <section className="lp-credit-union" aria-labelledby="credit-union-title">
            <div className="lp-credit-union-heading">
              <p className="lp-eyebrow">For Lafayette</p>
              <h3 id="credit-union-title">Built around<br />your credit union.</h3>
            </div>
            <ul className="lp-credit-union-benefits">
              <li><Check size={17} aria-hidden="true" /><div><h4>Make it your own.</h4><p>Co-branded or white-label, with Lafayette’s logo, colors and help lines.</p></div></li>
              <li><Check size={17} aria-hidden="true" /><div><h4>Tailor the training.</h4><p>Customize examples around the scams your members actually encounter.</p></div></li>
              <li><Check size={17} aria-hidden="true" /><div><h4>Feature relevant scams.</h4><p>Put local threats and your fraud team’s priorities front and center.</p></div></li>
              <li><Check size={17} aria-hidden="true" /><div><h4>Connect to your fraud reporting.</h4><p>Direct members to Lafayette’s existing reporting channels and support team.</p></div></li>
              <li><Check size={17} aria-hidden="true" /><div><h4>Support your frontline staff.</h4><p>A practical resource to share when a member asks, “Is this a scam?”</p></div></li>
            </ul>
          </section>
        </section>

        <section id="community" className="lp-community lp-section" aria-labelledby="community-title">
          <div className="lp-container">
            <SectionHeading number="02" label="The community" id="community-title" title={<>The same care.<br />Beyond your branches.</>}>
              Sponsor free access for senior centers near Lafayette branches. Lafayette chooses the centers; GranGuard sets up and runs each center’s site.
            </SectionHeading>
            <div className="lp-community-offer">
              <figure className="lp-center-map">
                <img src="/partners/lafayette-centers.svg" width="550" height="540" loading="lazy" alt="Maryland map with a close-up showing six proposed senior center locations in Montgomery County and Washington, DC. Numbers match the center list below." />
                <figcaption>Six proposed locations. Explore the center details below.</figcaption>
              </figure>
              <div className="lp-center-kit">
                <p className="lp-eyebrow">Every participating center receives</p>
                <ul>
                  <li><Check size={17} aria-hidden="true" /> A co-branded online resource with local help lines</li>
                  <li><Check size={17} aria-hidden="true" /> The course, checking tools and scam guides</li>
                  <li><Check size={17} aria-hidden="true" /> Setup, customization, hosting and content updates</li>
                </ul>
                <p className="lp-sponsor-line">Supported by Lafayette Federal Credit Union</p>
              </div>
            </div>
            <Disclosure title="Explore the proposed six centers" note="The opening shortlist">
              <ul className="lp-center-list">
                {CENTERS.map((center, index) => <li key={center.name}><div className="lp-center-list-label"><span className="lp-center-number" aria-label={`Map location ${index + 1}`}>{index + 1}</span><div><h3>{center.name}</h3><p>{center.operator}</p></div></div><span>{center.location}</span></li>)}
              </ul>
              <p className="lp-detail-note">Expansion options include Gaithersburg, Takoma Park, Prince George’s County, Northern Virginia and other DC centers.</p>
            </Disclosure>
            <p className="lp-community-note">Each center approves its participation and branding. Lafayette can choose alternatives to the suggested centers.</p>
          </div>
        </section>

        <section id="launch" className="lp-section lp-container" aria-labelledby="launch-title">
          <SectionHeading number="03" label="The launch" id="launch-title" title="Simple to launch">
            No integrations required. We host it, manage it and keep it updated. You approve the details and share it with members.
          </SectionHeading>
          <div className="lp-hosting-options" aria-label="Member site address options">
            <div><p className="lp-eyebrow">Ready now</p><Link to="/" className="lp-hosting-address">lafayette.granguard.org <ArrowRight size={16} aria-hidden="true" /></Link></div>
            <div><p className="lp-eyebrow">Or use your own domain</p><p className="lp-hosting-address">security.lfcu.org <span>Example</span></p></div>
          </div>
          <ol className="lp-launch-steps">
            <li><span className="lp-step-number">01</span><h3>Approve the details.</h3><p>Confirm the branding, training examples and fraud reporting links. Choose your launch date.</p></li>
            <li><span className="lp-step-number">02</span><h3>Share it with members.</h3><p>We supply the email copy, social posts, a poster file and a one-page guide. You share the link.</p></li>
            <li><span className="lp-step-number">03</span><h3>Leave the upkeep to us.</h3><p>We handle hosting, updates and maintenance. You get engagement summaries with totals, not member records.</p></li>
          </ol>
          <div className="lp-security">
            <Disclosure title="For your fraud and security team" note="No core integration. No access to member accounts.">
              <div className="lp-security-grid">
                <div><h3>Clear boundaries.</h3><p>Education and guidance, never a guarantee. No access to accounts, transactions or member records. Reporting shows counts, not identities.</p></div>
                <div><h3>Evidence you can follow.</h3><p>Library entries draw on FTC, FBI and other fraud reports. Scam checks explain what was assessed and why.</p></div>
                <div><h3>Ready for due diligence.</h3><p>Security documents and questionnaire answers on request. We’re working toward a SOC 2 Type II examination; a completed report is not yet available.</p></div>
              </div>
            </Disclosure>
          </div>
        </section>

        <section id="investment" className="lp-investment lp-section" aria-labelledby="investment-title">
          <div className="lp-container">
            <SectionHeading number="04" label="The investment" id="investment-title" title="Clear Pricing">
              Start with the member benefit. Add community sponsorship at the scale that fits.
            </SectionHeading>
            <div className="lp-pricing">
              <div className="lp-price-breakdown">
                <div className="lp-price-item"><div><h3>Member benefit</h3><p>Your branded platform, family access<br />and launch materials.</p><span>First year, with a $6,000 introductory discount · Renews at $30,000</span></div><strong>$24,000</strong></div>
                <div className="lp-price-item"><div><h3>Six-center sponsorship</h3><p>A co-branded site for each center, with setup,<br />hosting and content updates.</p><span>$2,000 per center / year</span></div><strong>$12,000</strong></div>
              </div>
              <div className="lp-total">
                <p className="lp-eyebrow">Member benefit + six centers</p>
                <p className="lp-total-price">$36,000</p>
                <p className="lp-total-period">for the first year</p>
                <div className="lp-total-renewal"><span>Annual renewal</span><strong>$42,000</strong></div>
                <p className="lp-total-deadline">Introductory offer available through October 31, 2026.</p>
              </div>
            </div>
            <p className="lp-founding-note">The first-year member program fee includes a $6,000 introductory discount from the $30,000 annual rate. Printed kits are optional and quoted separately. We’d welcome a joint launch announcement or a future case study; participation is optional, and anything featuring Lafayette’s name, logo or quotes needs Lafayette’s approval first.</p>
            <Disclosure title="Other community sponsorship options" note="From one center to county-wide">
              <div className="lp-table-wrap"><table className="lp-bundle-table"><caption className="sr-only">Annual community sponsorship options, in addition to the member benefit</caption><thead><tr><th scope="col">Coverage</th><th scope="col">Annual price</th><th scope="col">Per center</th></tr></thead><tbody>{BUNDLES.map(bundle => <tr key={bundle.name}><th scope="row">{bundle.name}</th><td>{bundle.annual}</td><td>{bundle.each}</td></tr>)}</tbody></table></div>
              <p className="lp-detail-note">Each option includes a co-branded site per center, with setup, hosting and content updates. Add centers to the six-center package at $2,000 per center each year.</p>
            </Disclosure>
          </div>
        </section>

        <section className="lp-final-word" aria-label="Closing thought">
          <p><span>Your fraud team protects the transaction.</span>{' '}<span>GranGuard helps protect the decision behind it.</span></p>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <GranGuardMark className="lp-footer-mark" />
          <p>Lee Mora, founder<br /><a href={`mailto:${GG_EMAIL}`}>{GG_EMAIL}</a></p>
          <p className="lp-private">Private proposal for Lafayette Federal Credit Union.<br />Prices valid through October 31, 2026.</p>
        </div>
      </footer>
    </div>
  )
}

function SectionHeading({ number, label, id, title, children }: { number: string; label: string; id: string; title: ReactNode; children: ReactNode }) {
  return <div className="lp-section-heading"><p className="lp-eyebrow">{number} <span>/</span> {label}</p><h2 id={id}>{title}</h2><p className="lp-section-copy">{children}</p></div>
}

function Benefit({ number, title, children }: { number: string; title: string; children: ReactNode }) {
  return <div className="lp-benefit"><span>{number}</span><div><h3>{title}</h3><p>{children}</p></div></div>
}

function Disclosure({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return <details className="lp-disclosure"><summary><span><strong>{title}</strong><small>{note}</small></span><ChevronDown size={20} aria-hidden="true" /></summary><div className="lp-disclosure-content">{children}</div></details>
}
